import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { SessionMonitor } from "@/components/SessionMonitor";
import { useSecurityLogger } from "@/hooks/useSecurityLogger";
import { supabase } from "@/integrations/supabase/client";

interface InstructorRouteProps {
  children: React.ReactNode;
}

export function InstructorRoute({ children }: InstructorRouteProps) {
  const { isAdmin, isInstructor, isLoading } = useUserRole();
  const navigate = useNavigate();
  const [shouldRender, setShouldRender] = useState(true);
  const redirectTimeout = useRef<NodeJS.Timeout>();
  const { logSecurityEvent } = useSecurityLogger();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    // Log unauthorized access attempt
    const logUnauthorizedAttempt = async () => {
      if (!isLoading && !isAdmin && !isInstructor && !hasLoggedRef.current) {
        hasLoggedRef.current = true;
        const { data: { user } } = await supabase.auth.getUser();
        
        await logSecurityEvent({
          event_type: "unauthorized_access",
          description: `Unauthorized instructor area access attempt by ${user?.email || 'unauthenticated user'}`,
          severity: "high",
          user_id: user?.id,
          was_blocked: true,
          details: {
            attempted_route: window.location.pathname,
            user_email: user?.email,
            timestamp: new Date().toISOString(),
          },
        });
      }
    };

    // Allow both admin and instructor access
    if (!isLoading && !isAdmin && !isInstructor) {
      logUnauthorizedAttempt();
      redirectTimeout.current = setTimeout(() => {
        setShouldRender(false);
        navigate("/dashboard");
      }, 300);
    } else {
      setShouldRender(true);
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    }

    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, [isAdmin, isInstructor, isLoading, navigate, logSecurityEvent]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if ((!isAdmin && !isInstructor) || !shouldRender) {
    return null;
  }

  return (
    <>
      <SessionMonitor isAdminRoute={false} />
      {children}
    </>
  );
}
